import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChecklistIcon from '@mui/icons-material/Checklist';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';

const LineProductionLogPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    line_log_overview: false,
    line_log_checklist: false,
    line_log_supply: false,
    line_log_yield: false,
    line_log_signatures: false
  });
  
  // Line log pages mapping
  const lineLogPages = [
    'line_log_overview', 
    'line_log_checklist', 
    'line_log_supply', 
    'line_log_yield', 
    'line_log_signatures'
  ];
  
  const pageNameMapping = {
    'line_log_overview': 'Overview Log Page',
    'line_log_checklist': 'Area Checklist Log Page',
    'line_log_supply': 'Supply Log Page',
    'line_log_yield': 'Yield Log Page',
    'line_log_signatures': 'Signatures Log Page'
  };
  
  const pageIconMapping = {
    'line_log_overview': <DashboardIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'line_log_checklist': <ChecklistIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'line_log_supply': <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'line_log_yield': <TrendingUpIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'line_log_signatures': <AssignmentIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderLineLogPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'line_log_overview') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.overview'));
    } else if (pageKey === 'line_log_checklist') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.checklist'));
    } else if (pageKey === 'line_log_supply') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.supply'));
    } else if (pageKey === 'line_log_yield') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.yield'));
    } else if (pageKey === 'line_log_signatures') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('production.line.log.signatures'));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit') && !p.name?.includes('supervisor') && !p.name?.includes('quality_control') && !p.name?.includes('final_release'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete'));
    
    // Special permissions for signatures
    const supervisorPerm = permissions.find(p => p.name === 'production.line.log.signatures.supervisor');
    const qualityControlPerm = permissions.find(p => p.name === 'production.line.log.signatures.quality_control');
    const finalReleasePerm = permissions.find(p => p.name === 'production.line.log.signatures.final_release');

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (editPerm || deletePerm || (pageKey === 'line_log_signatures' && (supervisorPerm || qualityControlPerm || finalReleasePerm))) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission - for all pages except signatures */}
            {editPerm && pageKey !== 'line_log_signatures' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'line_log_supply' && deletePerm) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission - only for line_log_supply */}
            {deletePerm && pageKey === 'line_log_supply' && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (pageKey === 'line_log_signatures' && (supervisorPerm || qualityControlPerm || finalReleasePerm)) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) {
                      newPermissions[deletePerm.id] = {};
                    }
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Signatures special permissions */}
            {pageKey === 'line_log_signatures' && (
              <>
                {supervisorPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: qualityControlPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Production Supervisor Signature
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can provide supervisor signature
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[supervisorPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[supervisorPerm.id]) {
                          newPermissions[supervisorPerm.id] = {};
                        }
                        newPermissions[supervisorPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
                {qualityControlPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: finalReleasePerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Quality Control Signature
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can provide quality control signature
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[qualityControlPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[qualityControlPerm.id]) {
                          newPermissions[qualityControlPerm.id] = {};
                        }
                        newPermissions[qualityControlPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
                {finalReleasePerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Final Release Signature
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can provide final release signature
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[finalReleasePerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[finalReleasePerm.id]) {
                          newPermissions[finalReleasePerm.id] = {};
                        }
                        newPermissions[finalReleasePerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {lineLogPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderLineLogPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default LineProductionLogPermissions;
